import { DropList, DropListItem, Highlighted, Modal, setAnnouncement } from '@starrez/starryui';
import { DisabledOpacity, PixelUnit } from '@starrez/starryui/dist/Theme';
import { color, Icon } from '@starrez/starryui9';
import * as React from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import styled from 'styled-components';
import { IAvailableFilter } from '..';
import { strings } from '../../../../.localisation';
import { getResultCountMessage } from '../../../utils/stringUtil';
import { WrapperDiv } from '../FilterBar';
import { useFilterBarContext } from '../FilterBar.context';

export const NoneFound = () => <NoneFoundWrapper>{strings.Search_Label_NoResultsFound}</NoneFoundWrapper>;

export interface IAddFilterButtonProps {
    onFilterTypeClick: (filterType: IAvailableFilter) => void;
    disabled?: boolean;
    showSearch?: boolean;
}

export const AddFilterButton = ({ onFilterTypeClick, disabled, showSearch }: IAddFilterButtonProps) => {
    const { filterTypes, doesItemHaveValue } = useFilterBarContext();
    const [visible, setVisible] = React.useState(false);
    const addButtonRef: any = React.useRef();
    const [searchString, setSearchString] = React.useState('');
    const { sayPolitely } = setAnnouncement();

    const orderByName = (dataSource: IAvailableFilter[]) => {
        dataSource.sort((a, b) => {
            if (a.name < b.name) {
                return -1;
            }
            if (a.name > b.name) {
                return 1;
            }
            return 0;
        });

        return dataSource;
    };

    const [selected, setSelected] = React.useState<IAvailableFilter[]>(orderByName(filterTypes));

    const isFilterAlreadyAdded = (item: IAvailableFilter) => {
        return doesItemHaveValue(item.name);
    };

    const handleFilterTypeClick = (item: IAvailableFilter) => {
        onFilterTypeClick(item);
        setVisible(false);
    };

    const handleSearched = (searched: string): Promise<void> => {
        return new Promise((resolve) => {
            setSearchString(searched);
            const regex = RegExp(searched, 'ig');
            const newData = orderByName(filterTypes)
                .filter((e) => e)
                .filter((e) => e.name.match(regex));
            setSelected(newData);
            resolve();

            sayPolitely(getResultCountMessage(newData.length) as string);
        });
    };

    const handleSearchCancelled = (): Promise<void> => {
        return new Promise((resolve) => {
            setSearchString('');
            setSelected(filterTypes);
            resolve();
        });
    };

    const handleCloseDropdown = () => {
        setSearchString('');
        setSelected(filterTypes);
        setVisible(false);
        if (addButtonRef.current) {
            addButtonRef.current.focus();
        }
    };

    React.useEffect(() => {
        if (visible) {
            document.addEventListener('click', handleCloseDropdown);
        }

        return () => {
            document.removeEventListener('click', handleCloseDropdown);
        };
    }, [visible]);

    const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setVisible(!visible);
    };

    return (
        <Manager>
            <Reference>
                {({ ref }) => (
                    <div ref={ref}>
                        <AddButton type="button" ref={addButtonRef} onClick={handleOnClick} disabled={disabled}>
                            <Icon icon="Plus" />
                            <span>{strings.FilterBar_Label_AddFilter}</span>
                        </AddButton>
                    </div>
                )}
            </Reference>
            <Modal DisableAutoFocus>
                <Popper placement="bottom-start">
                    {({ ref, style }) =>
                        visible && (
                            <WrapperDiv ref={ref} style={style}>
                                <DropList
                                    OnCloseMenu={handleCloseDropdown}
                                    ShowSearch={showSearch}
                                    OnSearch={handleSearched}
                                    OnSearchCancel={handleSearchCancelled}
                                >
                                    {selected.length > 0 ? (
                                        selected.map((e) => (
                                            <DropListItem key={e.name} Disabled={isFilterAlreadyAdded(e)} OnClick={() => handleFilterTypeClick(e)}>
                                                <Highlighted Text={e.name} Highlight={searchString} />
                                            </DropListItem>
                                        ))
                                    ) : (
                                        <NoneFound />
                                    )}
                                </DropList>
                            </WrapperDiv>
                        )
                    }
                </Popper>
            </Modal>
        </Manager>
    );
};

export const NoneFoundWrapper = styled.li`
    padding: 6px 14px;
    color: ${color.Grey.G60};
    font-weight: 500;
    font-size: 14px;
`;

export const AddButton = styled.button`
    display: flex;
    flex-direction: row;
    align-items: center;
    text-align: center;
    height: ${PixelUnit(4)};
    border: 0;
    border-radius: 4px;
    padding: 0 ${PixelUnit(1)};
    cursor: pointer;
    background: transparent;
    color: ${color.Blue.B60};
    font-style: normal;
    font-weight: bold;
    font-size: 14px !important;
    line-height: 1.3em;
    letter-spacing: 0.02em;

    &[disabled] {
        opacity: ${DisabledOpacity};
        pointer-events: none;
    }

    &:hover {
        background: ${color.Blue.B20};
    }
`;
